import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import './MatchDetailsPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import GameEndPlayerInfo from '../../GameEndContent/GameEndPlayerInfo/GameEndPlayerInfo';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {formatMatchDate} from '../../../utils/date/formatMatchDate';
import {PlayerIcon} from '../../coloredIcons/PlayerIcon/PlayerIcon';
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";
import {APP_VERSION} from '../../../constants/version';
import {getTutorialMatchInfo} from '../../../utils/tutorial/getTutorialMatchInfo';
import {getLocalMatchInfo} from '../../../utils/localMatch/getLocalMatchInfo';
import {MATCH_RECORDING_ACCESS, resolveMatchRecordingAccess} from '../../../utils/replay/matchRecordingAccess';
import {LocalGameIcon} from '../../coloredIcons/LocalGameIcon/LocalGameIcon';
import {formatTutorialStepNumber} from '../../../utils/tutorial/tutorialAnalyticsIds';
import {TutorialBookIcon} from '../../coloredIcons/TutorialBookIcon/TutorialBookIcon';
import {AI_ICON_PATH, AI_MATCH_TITLE} from '../../../configs/aiMatch';
import {getLegacyPlayerBonusConfig} from '../../../configs/playerBonusLegacy';

import FloatingBackButton from '../../elements/FloatingBackButton/FloatingBackButton';

const PAGE_ID = 'MatchDetailsPage';

// Строка «Тип» у промо-матча против ИИ
export const AI_MATCH_TYPE_LABEL = 'Рейтинговая, с ИИ';

function formatDuration(ms) {
  if (!ms || ms <= 0) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return `${hours} ч ${mins} мин`;
  }
  return `${minutes} мин ${seconds} сек`;
}

function MatchDetailsPage({matchId, onBack, onMainMenu, onPlayerClick, onWatchReplay}) {
  const currentUserId = useSelector((state) => state.private.auth.id);
  const isViewerAdmin = useSelector((state) =>
    Boolean(state.private.auth.isAdmin || state.private.auth.isSuperAdmin)
  );
  // Видеомейкер снимает ролики по чужим партиям: запись ему открыта для любого
  // сетевого матча. Но ТОЛЬКО запись, и без скрытых признаков ботов (сервер
  // вырезает их всем, кроме админа) — выгрузка файла по-прежнему админская
  const isViewerVideoMaker = useSelector((state) => Boolean(state.private.auth.isVideoMaker));
  const [match, setMatch] = useState(null);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isExporting, setIsExporting] = useState(false);

  usePageAnalytics('MatchDetailsPage');

  /**
   * Выгрузка записи матча в JSON — только админам.
   *
   * Кладем в файл не голый массив действий, а еще и карточку матча: по одному
   * массиву непонятно, кто играл, какими фракциями и чем кончилось, а разбирать
   * это глазами по сотням действий больно. Само поле, отряды и строения лежат
   * внутри записи (первым идет LOAD_STATE со снимком поля), поэтому файл
   * самодостаточен и не зависит от текущих конфигов полей.
   */
  const handleExportReplay = async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE}/matches/${matchId}/actions`, {credentials: 'include'});
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Не удалось получить запись матча');
      }
      const data = await response.json();

      const payload = {
        exportedAt: new Date().toISOString(),
        appVersion: APP_VERSION,
        match,
        actionsCount: (data.actions || []).length,
        actions: data.actions || [],
      };

      const blob = new Blob([JSON.stringify(payload, null, 2)], {type: 'application/json'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `strategikon-replay-${matchId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export replay error', e);
      alert(e.message || 'Не удалось выгрузить реплей');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        const res = await fetch(`${API_BASE}/matches/${matchId}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to fetch match');
        const data = await res.json();
        setMatch(data.match);
        setOwner(data.owner || null);
      } catch (e) {
        console.error('Match details error', e);
        setError('Не удалось загрузить информацию о матче');
      } finally {
        setLoading(false);
      }
    };

    fetchMatch();
  }, [matchId]);

  // Запись матча доступна админам, видеомейкерам (любого сетевого матча) и
  // тем, кто реально в нем участвовал; записи обучения и локальных партий —
  // только админам. Остальным кнопка не показывается вовсе. Правило — то же,
  // что проверяет сервер, одной функцией; сырую запись, со скрытыми признаками
  // ботов, получает только админ. Гость (детали матча открыты и ему, id у него
  // нет) участником не бывает
  const recordingAccess = resolveMatchRecordingAccess({
    match, viewerId: currentUserId, isViewerAdmin, isViewerVideoMaker,
  });
  // Матч-обучение: без метрик партии (игроки/раунды у сценарной главы не
  // информативны), тип — «Обучение». Запись прохождения главы — инструмент
  // разбора, где игроки бросают обучение: ее смотрят и выгружают только админы
  const isTutorialMatch = Boolean(match?.isTutorial);
  const tutorialInfo = getTutorialMatchInfo(match);
  const localInfo = getLocalMatchInfo(match);
  // Запись локальной партии — такой же инструмент разбора, как запись
  // обучения: смотрят и выгружают только админы, и есть она не у каждого
  // матча (старые, партия без записи) — сервер сообщает об этом
  // признаком hasRecording
  const isRecordedRunMatch = isTutorialMatch || Boolean(match?.isLocal);
  const hasRecordingToOpen = !isRecordedRunMatch || Boolean(match?.hasRecording);
  const canWatchReplay = recordingAccess !== MATCH_RECORDING_ACCESS.FORBIDDEN && hasRecordingToOpen;
  // Выгрузка файла — сырая запись: только тому, кому сервер отдает ее как есть
  const canExportReplay = recordingAccess === MATCH_RECORDING_ACCESS.FULL && hasRecordingToOpen;

  return (
    <>
      <FloatingBackButton onBack={onBack} pageId={PAGE_ID}/>

      <h1 className="lobby-page-title">Детали матча</h1>

      {loading && <p className="match-details-status">Загрузка...</p>}
      {error && <p className="match-details-status match-details-status_error">{error}</p>}

      {!loading && !error && match && (
        <>
          <div className="match-details-info">
            {tutorialInfo?.isRecordedFromStart ? (
              <>
                {/* Запись об обучении создается на старте главы: видно и
                    начало, и пройдена ли глава (недоигранные видны всем) */}
                <div className="match-details-row">
                  <span className="match-details-label">Начато</span>
                  <span className="match-details-value">{formatMatchDate(match.startedAt)}</span>
                </div>
                <div className="match-details-row">
                  <span className="match-details-label">Статус</span>
                  <span
                    className={`match-details-value${tutorialInfo.isUnfinished ? ' match-details-value_unfinished' : ''}`}
                  >
                    {tutorialInfo.isUnfinished ? 'Не завершено' : 'Пройдено'}
                  </span>
                </div>
                {!tutorialInfo.isUnfinished && (
                  <div className="match-details-row">
                    <span className="match-details-label">Завершено</span>
                    <span className="match-details-value">{formatMatchDate(match.finishedAt)}</span>
                  </div>
                )}
                {tutorialInfo.isUnfinished && match.lastActivityAt && (
                  <div className="match-details-row">
                    <span className="match-details-label">Последнее действие</span>
                    <span className="match-details-value">{formatMatchDate(match.lastActivityAt)}</span>
                  </div>
                )}
                {tutorialInfo.durationMs !== null && (
                  <div className="match-details-row">
                    <span className="match-details-label">Длительность</span>
                    <span className="match-details-value">{formatDuration(tutorialInfo.durationMs)}</span>
                  </div>
                )}
                {/* Шаг, на котором глава брошена, виден всем — как и в плашке
                    истории; технический идентификатор шага — только админам */}
                {tutorialInfo.isUnfinished && tutorialInfo.stepNumber && (
                  <div className="match-details-row">
                    <span className="match-details-label">Дошёл до</span>
                    <span className="match-details-value match-details-tutorial-step">
                      шаг {tutorialInfo.stepNumber} из {tutorialInfo.stepsCount}
                      {isViewerAdmin && tutorialInfo.stepId && (
                        <span className="match-details-tutorial-step-id">
                          {formatTutorialStepNumber(tutorialInfo.stepIndex)}_{tutorialInfo.stepId}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </>
            ) : localInfo?.isUnfinished ? (
              <>
                {/* Незавершенная локальная партия (видна всем): окончания
                    у нее нет — показываем старт и время последней записанной
                    пачки */}
                <div className="match-details-row">
                  <span className="match-details-label">Начато</span>
                  <span className="match-details-value">{formatMatchDate(match.startedAt)}</span>
                </div>
                <div className="match-details-row">
                  <span className="match-details-label">Статус</span>
                  <span className="match-details-value match-details-value_unfinished">Не завершён</span>
                </div>
                {match.lastActivityAt && (
                  <div className="match-details-row">
                    <span className="match-details-label">Последнее действие</span>
                    <span className="match-details-value">{formatMatchDate(match.lastActivityAt)}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="match-details-row">
                <span className="match-details-label">Завершён</span>
                <span className="match-details-value">{formatMatchDate(match.finishedAt)}</span>
              </div>
            )}
            {!isTutorialMatch && (
              <>
                <div className="match-details-row">
                  <span className="match-details-label">Длительность</span>
                  {/* У незавершенной партии длительность считается по записи:
                      в самом матче она проставляется только итогами */}
                  <span className="match-details-value">
                    {formatDuration(localInfo?.isUnfinished ? localInfo.durationMs : match.durationMs)}
                  </span>
                </div>
                <div className="match-details-row">
                  <span className="match-details-label">Игроков</span>
                  <span className="match-details-value match-details-player-count">
                    {match.playerCount}
                    <PlayerIcon
                      className="match-details-player-count-icon"
                      color="var(--text-primary-color)"
                      isSmall
                    />
                  </span>
                </div>
                {/* Незавершенная локальная партия: до какого раунда дошла
                    запись (пишется раз в раунд) */}
                {localInfo?.isUnfinished && (
                  <div className="match-details-row">
                    <span className="match-details-label">Дошёл до</span>
                    <span className="match-details-value">
                      {localInfo.lastRecordedRound
                        ? `раунд ${localInfo.lastRecordedRound}${localInfo.finalRound ? ` из ${localInfo.finalRound}` : ''}`
                        : 'первого раунда'}
                    </span>
                  </div>
                )}
                {/* У незавершенной партии сыгранных раундов нет: finalRound
                    у нее — лимит партии, а докуда она дошла, сказано выше */}
                {!localInfo?.isUnfinished && (
                  <div className="match-details-row">
                    <span className="match-details-label">Раундов</span>
                    {/* Сколько раундов реально сыграли: при досрочном конце по
                        отрыву в очках finalRound остается лимитом партии. У
                        матчей, сохраненных до появления endRound, показываем
                        прежнее значение */}
                    <span className="match-details-value">{match.endRound ?? match.finalRound ?? '—'}</span>
                  </div>
                )}
              </>
            )}
            <div className="match-details-row">
              <span className="match-details-label">Тип</span>
              <span className="match-details-value match-details-type">
                {isTutorialMatch ? (
                  <>
                    {/* Недоигранная глава — красная книжка, как в плашке истории */}
                    <TutorialBookIcon
                      className="match-details-type-icon"
                      isUnfinished={Boolean(tutorialInfo?.isUnfinished)}
                    />
                    {match.tutorialChapterTitle ? `Обучение, ${match.tutorialChapterTitle}` : 'Обучение'}
                  </>
                ) : match.isLocal ? (
                  <>
                    {/* Незавершенная партия — красный значок, как в плашке истории */}
                    <LocalGameIcon
                      className="match-details-type-icon"
                      isUnfinished={Boolean(localInfo?.isUnfinished)}
                    />
                    {localInfo?.isUnfinished ? 'Локальная, не завершена' : 'Локальная'}
                  </>
                ) : match.isAiMatch ? (
                  // Промо-матч против ИИ рейтинговый (isRanked тоже true), но
                  // вместо звездочки — метка ИИ: это его главное отличие
                  <>
                    <img
                      className="match-details-type-icon"
                      src={AI_ICON_PATH}
                      alt={AI_MATCH_TYPE_LABEL}
                      title={AI_MATCH_TITLE}
                    />
                    {AI_MATCH_TYPE_LABEL}
                  </>
                ) : match.isRanked ? (
                  <>
                    <img
                      className="match-details-type-icon"
                      src="/img/icons/star_rating.png"
                      alt="Рейтинговая"
                    />
                    Рейтинговая
                  </>
                ) : (
                  'Обычная'
                )}
              </span>
            </div>
            {match.isLocal && owner && (
              <div className="match-details-row">
                <span className="match-details-label">Владелец</span>
                <span className="match-details-value match-details-owner">{owner.name}</span>
              </div>
            )}
          </div>

          <h2 className="match-details-players-title">Игроки</h2>
          <div className="match-details-players">
            {match.players.map((player, index) => {
              const playerId = player.userId || player.playerId;
              // За явным ботом нет ни аккаунта, ни профиля — открывать нечего
              const isVisibleBot = Boolean(player.isVisibleBot);
              const clickedUserId = match.isLocal && owner ? owner.id : player.userId;
              const onClick = () => {
                MENU_SOUND_MANAGER.playMenuButtonClick();
                const clickedUserName = match.isLocal && owner ? owner.name : player.name;
                onPlayerClick?.(clickedUserId, clickedUserName)
              }
              return (
                <GameEndPlayerInfo
                  key={playerId}
                  customClassName="match-details-player-info"
                  // Профиля нет у явного бота и у бота обучения (аккаунта за
                  // ним нет) — открывать нечего
                  onClick={isVisibleBot || !clickedUserId ? undefined : onClick}
                  // У недоигранной главы и у незавершенной локальной
                  // партии мест нет: без этого значки мест встали бы просто
                  // по порядку игроков
                  noPlaceIcon={Boolean(tutorialInfo?.isUnfinished || localInfo?.isUnfinished)}
                  // Очков у незавершенной локальной партии тоже нет: на старте
                  // сервер пишет всем 0, и это выглядело бы итоговым счетом.
                  // У обучения очков нет и у пройденных глав — там как было
                  noFinalWinPoints={Boolean(localInfo?.isUnfinished)}
                  player={{
                    id: playerId,
                    name: player.name,
                    color: player.color,
                    factionId: player.factionId,
                    selectedSkin: player.selectedSkin,
                    finalWinPoints: player.finalWinPoints,
                    // В локальных матчах боты видны владельцу как раньше;
                    // в сетевых синтетические игроки помечаются только админам
                    isBot: Boolean(player.isBot) && (match.isLocal || isViewerAdmin),
                    // «Явный» бот рейтингового матча, в отличие от isBot, виден
                    // всем: он и в самом матче был помечен ботом для всех
                    isVisibleBot,
                    // ИИ-игрок промо-матча: метка ИИ из записи матча. В отличие
                    // от явного бота за ним настоящий аккаунт — профиль
                    // открывается, как у людей
                    isAi: Boolean(player.isAi),
                    // Лига такого бота — из записи матча: значок у него свой
                    // на каждую лигу
                    leagueId: player.leagueId,
                    // Бот локальной партии помечен так же, как был помечен в
                    // самой партии. Признак берется из записи матча, а не из
                    // isLocal: сюжетные миссии тоже сохраняются локальными
                    // матчами, но их ботов значок не помечает
                    isLocalBot: Boolean(player.isLocalBot),
                    // Уровень бонуса и зафиксированные числа: по ним рисуется
                    // иконка значка и его подсказка (см. getBotMarkIconPath).
                    // bonusConfig — из записи матча, чтобы правка конфига не
                    // переписывала прошлое (см. getPlayerBonusValues). У
                    // локальных матчей до 29.10 чисел в записи нет — тогда
                    // действовали прежние (LEGACY_PLAYER_BONUS_CONFIG)
                    bonus: player.bonus,
                    bonusConfig: player.bonusConfig
                      || (match.isLocal ? getLegacyPlayerBonusConfig(player.bonus) || undefined : undefined),
                    // Прибавка явного бота к росту отрядов — тоже из записи
                    // матча (строка подсказки значка, см. getVisibleBotTitle)
                    botSupplySizeBonus: player.botSupplySizeBonus,
                    ratingDelta: match.isLocal ? undefined : player.originalRatingDelta,
                    rating: match.isLocal ? undefined : player.rating,
                    // У матча-обучения нумены не начисляются — «Награды: 0»
                    // не показываем, а сундук/премиум/фракцию показываем
                    numens: match.isLocal || isTutorialMatch ? undefined : player.numens,
                    chests: match.isLocal ? undefined : player.chests,
                    skins: match.isLocal ? undefined : player.skins,
                    premiumRewardDays: isTutorialMatch ? player.premiumRewardDays : undefined,
                    factionRewardId: isTutorialMatch ? player.factionRewardId : undefined,
                    isDoubleReward: match.isLocal ? undefined : Boolean(player.isDoubleReward),
                    isPremiumReward: match.isLocal ? undefined : Boolean(player.isPremiumReward),
                  }}
                  index={index}
                  // Место из записи матча: при ничьей оно общее у нескольких игроков
                  place={player.place > 0 ? player.place : undefined}
                  isRanked={match.isRanked}
                  isCompactMode={false}
                  isBlackOutline={true}
                  forceSeparateRewardsRow={true}
                  currentUserId={match.isLocal ? null : currentUserId}
                />
              )
            })}
          </div>
        </>
      )}

      <div className="main-menu-buttons">
        {!loading && !error && match && onWatchReplay && canWatchReplay && (
          <PageButton
            onClick={() => onWatchReplay(matchId)}
            pageId={PAGE_ID}
            buttonId="watch_replay"
          >
            Посмотреть запись
          </PageButton>
        )}
        {/* Выгрузка записи — админский инструмент: файл нужен, чтобы строить на
            реальных партиях интерактивные демо для новичков */}
        {!loading && !error && match && canExportReplay && (
          <PageButton
            onClick={handleExportReplay}
            pageId={PAGE_ID}
            buttonId="export_replay"
            disabled={isExporting}
          >
            {isExporting ? 'Выгружаю...' : 'Выгрузить реплей'}
          </PageButton>
        )}
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
        {onMainMenu && (
          <PageButton onClick={onMainMenu} pageId={PAGE_ID} buttonId="main_menu">
            Главное меню
          </PageButton>
        )}
      </div>
    </>
  );
}

export default MatchDetailsPage;
